import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { getValuesFromEnum } from "@app/class/Utilities";
import { FileUploadComponent } from "@app/directives/_forms/fileUpload/fileUpload.component";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { VTabsComponent } from "@app/directives/_layout/v-tabs/v-tabs.component";
import { VTableSelectionType } from "@app/enum/VTableSelectionType";
import { IPeripheral, IUnit } from "@app/interfaces/IPeripheral";
import { IProduct } from "@app/interfaces/IProduct";
import { ISim, SimType } from "@app/interfaces/ISim";
import { ISimMapping } from "@app/interfaces/ISimMapping";
import { IUser } from "@app/interfaces/IUser";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { EngineerJobItemType, ImportStrategyReference, ImportType } from "@app/main/modules/foundation/foundation.enums";
import { ICsvImportCompletionResultDTO, ICsvImportValidationResultDTO, IImportValidationDTO, IJobItemValidationResultDTO, ImportValidationError, SimToSerialMappings } from "@app/main/modules/foundation/foundation.interfaces";
import { IUserGroup } from "@app/main/modules/foundation/_models/IUserGroup";
import { AccountService } from "@app/main/modules/foundation/_services/account.service";
import { FoundationCsvImporterService } from "@app/main/modules/foundation/_services/foundation-csv-importer.service";
import { UserGroupService } from "@app/main/modules/foundation/_services/user-group.service";
import { CurrentUserService } from "@app/services/currentUser.service";
import { ProductService } from "@app/services/product.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import { ErrorType } from "../../_enums/ErrorType";
import { IReseller } from "../../_models/IReseller";
import './foundation-csv-importer.component.scss';

@UntilDestroy()
@Component({
    selector: 'foundation-csv-importer',
    template: require('./foundation-csv-importer.component.html')
})
export class FoundationCsvImporterComponent implements OnInit {
    @Input() importStrategyRef: ImportStrategyReference;
    @Input() jobId: number;
    @Input() accountId: number;
    @Input() onFinishRedirectTo: string;
    @Input() importType: ImportType;

    @Output() modelSave = new EventEmitter<ICsvImportCompletionResultDTO>();
    @Output() onFinishCallback = new EventEmitter<void>();

    // Global component
    selectedFile: File | null = null;
    importStrategyReference = ImportStrategyReference;

    importValidationResult: ICsvImportValidationResultDTO = null;
    validationCompleted: boolean;
    importCount: number;
    completionString: string;

    loading: boolean = false;
    dialog: VDialogRef;

    vTableSelectionType = VTableSelectionType.Single;

    fileSetupErrors: ImportValidationError[];
    mainAssetErrors: ImportValidationError[];

    // Device import specific
    products: IProduct[];
    selectedProduct: IProduct;
    selectedTypeString: string;

    // Sim import specific
    selectedSimType = SimType.WirelessLogic;
    simType = SimType;
    simTypeStrings = getValuesFromEnum(SimType);

    preMappedSimsToSerial: SimToSerialMappings[];
    newSimsToCreate: ISim[];

    fatalSimErrors: ImportValidationError[];
    warningSimErrors: ImportValidationError[];
    existingMappingsToEnd: ISimMapping[];

    // Engineer import specific
    groups: IUserGroup[];
    selectedUserGroup: IUserGroup;
    currentUser: IUser;

    // Job item import specific
    selectedJobItemType: EngineerJobItemType = EngineerJobItemType.Install;
    engineerJobItemType = EngineerJobItemType;
    visitReasonStrings = Object.values(EngineerJobItemType).filter(s => !isNaN(Number(s))).map(s => Number(s));
    resellerMap: Map<number, IReseller>;
    jobReasonMap: Map<EngineerJobItemType, string>;

    // HTML children
    @ViewChild('completionConfirmation') completionConfirmation: TemplateRef<any>;
    @ViewChild('importRequestConfirmation') importRequestConfirmation: TemplateRef<any>;
    @ViewChild('fileUpload') fileUpload: FileUploadComponent;
    @ViewChild('vTabsComponent') vTabsComponent: VTabsComponent;

    // table setups
    jobItemImportTableColumns: IVTableColumn<IJobItemValidationResultDTO>[]
    jobItemImportTableRows: IVTableRow[];

    engineerImportTableColumns: IVTableColumn<IUser>[];
    engineerImportTableRows: IVTableRow[];

    unitImportTableColumns: IVTableColumn<IUnit>[];
    unitImportTableRows: IVTableRow[];

    peripheralImportTableColumns: IVTableColumn<IPeripheral>[];
    peripheralImportTableRows: IVTableRow[];

    deviceErrorTableColumns: IVTableColumn<ImportValidationError>[];
    deviceErrorTableRows: IVTableRow[];

    simErrorTableColumns: IVTableColumn<ImportValidationError>[];
    simErrorTableRows: IVTableRow[];

    simImportTableColumns: IVTableColumn<ISim>[];
    simImportTableRows: IVTableRow[];

    productTableColumns: IVTableColumn<IProduct>[];
    productTableRows: IVTableRow[];

    engineerGroupTableColumns: IVTableColumn<IUserGroup>[];
    engineerGroupTableRows: IVTableRow[];

    jobReasonTableColumns: IVTableColumn<number>[];
    jobReasonTableRows: IVTableRow[];

    constructor(
        private readonly foundationCsvImporterService: FoundationCsvImporterService,
        private readonly productService: ProductService,
        private readonly vTableService: VTableService,
        private readonly dialogService: DialogService,
        private readonly translationService: TranslationService,
        private readonly userGroupService: UserGroupService,
        private readonly currentUserService: CurrentUserService,
        private readonly accountService: AccountService,) { }

    ngOnInit(): void {
        this.setupComponent();
    }

    private setupComponent() {
        this.currentUserService.getCurrentUser().then(currentUser => {
            this.currentUser = currentUser;

            this.createSelectionTables();
            this.createValidationTables();

            switch (this.importStrategyRef) {
                case ImportStrategyReference.Device:
                    this.initProductTable();
                    break;
                case ImportStrategyReference.Engineer:
                    this.initEngineerGroupTable();
                    break;
                case ImportStrategyReference.JobItem:
                    this.initJobReasonTable();
                default:
                    break;
            }
        });
    }

    private initEngineerGroupTable(): void {
        combineLatest([
            this.accountService.getResellers(),
            this.userGroupService.getUserGroups()
        ]).pipe(untilDestroyed(this)).subscribe(([resellers, userGroups]) => {
            this.resellerMap = new Map(resellers.map(reseller => [reseller.Id, reseller]));

            this.groups = userGroups;

            if (this.currentUser.IsReseller && !!this.groups.length) {
                this.groups = this.groups.filter(g => this.currentUser.ResellerIds.some(resellerId => resellerId === g.ResellerId));
            }

            if (!this.groups.length) {
                return;
            }

            this.selectedUserGroup = this.groups[0];

            this.engineerGroupTableRows = this.vTableService.createTableRows(this.groups, this.engineerGroupTableColumns, 0);
        });
    }

    private initProductTable(): void {
        this.productService.getProducts().then(products => {
            this.products = products.filter(p => p.HasSerial == true && p.Visible == true);

            if (!this.products.length) {
                return;
            }

            switch (this.importType) {
                case ImportType.Peripheral:
                    this.products = this.products.filter(p =>
                        p.GroupId === ProductVendor.AAMP ||
                        p.GroupId === ProductVendor.Streamax ||
                        p.GroupId === ProductVendor.RTL ||
                        p.GroupId === ProductVendor.Alcolock ||
                        p.GroupId === ProductVendor.TachoSys ||
                        p.GroupId === ProductVendor.SevenTelematics);
                    break;
                case ImportType.Unit:
                    this.products = this.products.filter(p =>
                        p.GroupId === ProductVendor.Calamp ||
                        p.GroupId === ProductVendor.Queclink);
                    break;
                default:
            }

            this.productService.sortProductsByGroup(this.products);

            this.selectedProduct = this.products[0];

            this.productTableRows = this.vTableService.createTableRows(this.products, this.productTableColumns, 0);
        });
    }

    private initJobReasonTable(): void {
        this.jobReasonTableRows = this.vTableService.createTableRows(this.visitReasonStrings, this.jobReasonTableColumns, 0)
    }

    readDeviceImportFile(event: any): void {
        this.importValidationResult = null;
        this.validationCompleted = false;

        if (event === undefined) {
            this.selectedFile = null;
        } else {
            this.selectedFile = event.file;
        }
    }

    validateImportFile() {
        this.loading = true;
        this.importValidationResult = null;
        this.validationCompleted = false;
        this.selectedFile.text().then(string => {
            const toSend: IImportValidationDTO = {
                CsvString: string,
                ImportStrategyReference: this.importStrategyRef,
                ProductId: this.selectedProduct?.Id,
                SimType: this.selectedSimType,
                ProductGroupId: this.selectedProduct?.GroupId || null,
                EngineerJobItemType: this.selectedJobItemType,
                JobId: this.jobId,
                AccountId: this.accountId
            }

            return this.foundationCsvImporterService.validateFile(toSend).subscribe(validationResult => {
                this.importCount = 0;
                var modelsJson = validationResult.ModelsToImportJson ?? null;

                if (!!modelsJson) {
                    if (this.importStrategyRef == ImportStrategyReference.Device) {
                        this.setupSimValidationReturn(validationResult);
                    }

                    switch (validationResult.ImportType) {
                        case ImportType.Peripheral:
                            this.setupPeripheralValidationReturn(validationResult, modelsJson);
                            break;
                        case ImportType.Unit:
                            this.setupUnitValidationReturn(validationResult, modelsJson);
                            break;
                        case ImportType.Engineer:
                            this.setupEngineerValidationReturn(validationResult, modelsJson);
                            break;
                        case ImportType.JobItem:
                            this.setupJobItemValidationReturn(validationResult, modelsJson);
                            break;
                        default:
                            break;
                    }
                }

                this.importValidationResult = validationResult;

                this.setCompletionString();

                this.setupAssetErrorTable();

                this.validationCompleted = true;
                this.loading = false;
            });
        });
    }

    onFinish(): void {
        this.onFinishCallback.emit();
    }

    private setupAssetErrorTable(): void {
        this.mainAssetErrors = this.importValidationResult.ImportValidationErrors?.filter(err => err.ErrorType == ErrorType.MainAsset);
        this.fileSetupErrors = this.importValidationResult.ImportValidationErrors?.filter(err => err.ErrorType == ErrorType.FileSetup);

        this.deviceErrorTableRows = this.vTableService.createTableRows(this.mainAssetErrors, this.deviceErrorTableColumns);
    }

    private setupSimValidationReturn(validationResult: ICsvImportValidationResultDTO): void {
        this.newSimsToCreate = validationResult.SimsToCreate;
        this.preMappedSimsToSerial = JSON.parse(validationResult.MappingsToCreateJson) as SimToSerialMappings[];
        this.existingMappingsToEnd = JSON.parse(validationResult.MappingsToEndJson) as ISimMapping[];

        const simErrors = validationResult.ImportValidationErrors?.filter(err => err.ErrorType != ErrorType.MainAsset);

        if (simErrors?.length) {
            this.fatalSimErrors = simErrors.filter(err => err.ErrorType == ErrorType.SimFatal);
            this.warningSimErrors = simErrors.filter(err => err.ErrorType == ErrorType.SimWarning);

            this.simErrorTableRows = this.vTableService.createTableRows(this.warningSimErrors.concat(this.fatalSimErrors), this.simErrorTableColumns);
        }

        if (this.newSimsToCreate?.length) {
            this.simImportTableRows = this.vTableService.createTableRows(this.newSimsToCreate, this.simImportTableColumns);
        }
    }

    closeDialog(): void {
        this.vTabsComponent.stepPrev(true);
        this.loading = false;
        this.dialog.close();
    }

    confirmUpload(): void {
        this.loading = true;
        this.dialog = this.dialogService.open(this.importRequestConfirmation, null);
    }

    backToStart(): void {
        this.selectedFile = null;
        this.fileUpload.clearFile();
        this.vTabsComponent.stepToBeginning();
    }

    UploadFile(): void {
        this.dialog.close();

        const returnModels = this.importStrategyRef !== ImportStrategyReference.Engineer ?
            this.importValidationResult.ModelsToImportJson :
            JSON.stringify(this.importValidationResult.engineers);

        this.foundationCsvImporterService.saveFile({
            ImportStrategyReference: this.importStrategyRef,
            ModelsToCreate: returnModels,
            ProductGroupId: this.selectedProduct?.GroupId,
            ResellerId: this.selectedUserGroup?.ResellerId,
            SimsToCreate: this.newSimsToCreate,
            MappingsToCreateJson: this.importValidationResult.MappingsToCreateJson,
            MappingsToEndJson: this.importValidationResult.MappingsToEndJson
        }).subscribe((completionResult) => {

            this.loading = false;
            this.modelSave.emit(completionResult);
        });
    }

    confirmCompletion(): void {
        this.importValidationResult = null;
        this.validationCompleted = false;
        this.selectedFile = null;
        this.fileUpload.clearFile();
        this.dialog.close();
    }

    onTabSelect($event: VTabComponent): void {
        switch ($event.tabIndex) {
            case 0:
                this.fatalSimErrors = [];
                this.warningSimErrors = [];
                this.newSimsToCreate = [];
                this.preMappedSimsToSerial = [];
                break;
            case 1:
                this.validateImportFile();
                break;
            case 2:
                this.confirmUpload();
                break;
            default:
                break;
        }
    }

    onProductTableRowClicked($event: IVTableRow): void {
        this.selectedProduct = this.products.find(p => p.Id == $event.data.Id);
    }

    onEngineerGroupTableRowClicked($event: IVTableRow): void {
        this.selectedUserGroup = this.groups.find(g => g.Id == $event.data.Id);
    }

    onjobReasonTableRowClicked($event: IVTableRow): void {
        this.selectedJobItemType = $event.data;
    }

    setCompletionString(): void {
        this.completionString = this.importValidationResult?.engineers?.length ?
            this.translationService.t('CsvImporter.EngineerCompletionString', [this.importCount, this.selectedTypeString]) :
            this.translationService.t('CsvImporter.CompletionString', [this.importCount, this.selectedTypeString]);
    }

    get canImport(): boolean {
        return this.importValidationResult?.peripherals?.length >= 1 ||
            this.importValidationResult?.units?.length >= 1 ||
            this.importValidationResult?.engineers?.length >= 1 ||
            this.importValidationResult?.jobItems?.length >= 1;
    }

    get isDeviceImport(): boolean {
        return this.importStrategyRef === ImportStrategyReference.Device;
    }

    /// ------------------------------

    private setupPeripheralValidationReturn(validationResult: ICsvImportValidationResultDTO, modelsJson: string) {
        validationResult.peripherals = JSON.parse(modelsJson) as IPeripheral[];
        this.peripheralImportTableRows = this.vTableService.createTableRows(validationResult.peripherals, this.peripheralImportTableColumns);
        this.importCount = validationResult.peripherals.length;
        this.selectedTypeString = this.selectedProduct.Description;
    }

    private setupUnitValidationReturn(validationResult: ICsvImportValidationResultDTO, modelsJson: string) {
        validationResult.units = JSON.parse(modelsJson) as IUnit[];
        this.unitImportTableRows = this.vTableService.createTableRows(validationResult.units, this.unitImportTableColumns);
        this.importCount = validationResult.units.length;
        this.selectedTypeString = this.selectedProduct.Description;
    }

    private setupEngineerValidationReturn(validationResult: ICsvImportValidationResultDTO, modelsJson: string) {
        validationResult.engineers = JSON.parse(modelsJson) as IUser[];
        this.engineerImportTableRows = this.vTableService.createTableRows(validationResult.engineers, this.engineerImportTableColumns);
        this.importCount = validationResult.engineers.length;
        this.selectedTypeString = this.selectedUserGroup.Description;
        validationResult.engineers.forEach(e => {
            e.UserGroupId = this.selectedUserGroup.Id;
        });
    }

    private setupJobItemValidationReturn(validationResult: ICsvImportValidationResultDTO, modelsJson: string) {
        validationResult.jobItems = JSON.parse(modelsJson) as IJobItemValidationResultDTO[];
        this.jobItemImportTableRows = this.vTableService.createTableRows(validationResult.jobItems, this.jobItemImportTableColumns);
        this.importCount = validationResult.jobItems.length;
        this.selectedTypeString = this.translationService.translateEnum(EngineerJobItemType, "EngineerJobItemType", this.selectedJobItemType);
    }

    private createValidationTables(): void {
        this.createDeviceErrorTable();
        this.createSimErrorTable();
        this.createSimImportTable();
        this.createPeripheralImportTable();
        this.createUnitImportTable();
        this.createEngineerImportTable();
        this.createJobItemImportTable();
    }

    private createSelectionTables(): void {
        this.createProductTable();
        this.createEngineerGroupTable();
        this.createJobReasonTable();
    }

    private createJobReasonTable(): void {
        this.jobReasonTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.VisitType" }),
                createCellFunction: (enumNumber) => this.vTableService.createEnumCell(EngineerJobItemType, "EngineerJobItemType", enumNumber)
            }
        ];
    }

    private createEngineerGroupTable(): void {
        this.engineerGroupTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.ParentCompany" }),
                createCellFunction: (userGroup) => this.vTableService.createTableCell(userGroup.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reseller" }),
                createCellFunction: (userGroup) => this.vTableService.createTableCell(this.resellerMap.get(userGroup.ResellerId)?.Description || `AddSecure UK`)
            }
        ];
    }

    private createProductTable(): void {
        this.productTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Description" }),
                createCellFunction: (product) => this.vTableService.createTableCell(product.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Supplier", sorted: true }),
                createCellFunction: (product) => this.vTableService.createTableCell(ProductVendor[product.GroupId])
            }
        ];
    }

    private createDeviceErrorTable(): void {

        this.deviceErrorTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 50 }),
                createCellFunction: (validationError) => this.vTableService.createStatusCell(validationError.ErrorType == ErrorType.SimWarning ? VTableRowStatus.Warning : VTableRowStatus.Error)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Line", width: 70, sorted: true }),
                createCellFunction: (validationError) => this.vTableService.createTableCell(validationError.LineNumber)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Error" }),
                createCellFunction: (validationError) => this.vTableService.createTableCell(validationError.Message)
            }
        ];
    }

    private createSimErrorTable(): void {

        this.simErrorTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 50 }),
                createCellFunction: (validationError) => this.vTableService.createStatusCell(validationError.ErrorType == ErrorType.SimFatal ? VTableRowStatus.Error : VTableRowStatus.Warning)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Line", width: 70, sorted: true }),
                createCellFunction: (validationError) => this.vTableService.createTableCell(validationError.LineNumber)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Error" }),
                createCellFunction: (validationError) => this.vTableService.createTableCell(validationError.Message)
            }
        ];
    }

    private createSimImportTable(): void {

        this.simImportTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 60 }),
                createCellFunction: (sim) => this.vTableService.createStatusCell(!!this.warningSimErrors?.find(err => err.Metadata == sim.Iccid) ? VTableRowStatus.Warning : VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createTableHeader({ title: "ICCID" }),
                createCellFunction: (sim) => this.vTableService.createTableCell(sim.Iccid)
            },
            {
                header: this.vTableService.createTableHeader({ title: "IMSI" }),
                createCellFunction: (sim) => this.vTableService.createTableCell(sim.Imsi)
            },
            {
                header: this.vTableService.createTableHeader({ title: "MSISDN" }),
                createCellFunction: (sim) => this.vTableService.createTableCell(sim.Msisdn, { classes: "validationWarning" })
            }
        ];
    }

    private createPeripheralImportTable(): void {

        this.peripheralImportTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 60 }),
                createCellFunction: () => this.vTableService.createStatusCell(VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Serial", width: 200 }),
                createCellFunction: (device) => this.vTableService.createTableCell(device.Serial)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.AssociatedIccid" }),
                createCellFunction: (device) => this.vTableService.createTableCell(this.preMappedSimsToSerial?.find(mapping => mapping.Item1 == device.Serial)?.Item2 ?? "--")
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.NewSim", width: 90 }),
                createCellFunction: (device) => this.vTableService.createTableCell(
                    !!this.preMappedSimsToSerial?.find(map => map.Item1 == device.Serial) &&
                        !!this.newSimsToCreate?.find(sim => sim.Iccid == this.preMappedSimsToSerial?.find(mapping => mapping.Item1 == device.Serial)?.Item2) ? "Yes" : "No")
            }
        ];
    }

    private createUnitImportTable(): void {

        this.unitImportTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 60 }),
                createCellFunction: () => this.vTableService.createStatusCell(VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.Serial", width: 200 }),
                createCellFunction: (unit) => this.vTableService.createTableCell(unit.Serial)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.AssociatedIccid" }),
                createCellFunction: (unit) => this.vTableService.createTableCell(this.preMappedSimsToSerial?.find(mapping => mapping.Item1 == unit.Serial)?.Item2 ?? "--")
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.NewSim", width: 90 }),
                createCellFunction: (unit) => this.vTableService.createTableCell(
                    !!this.preMappedSimsToSerial?.find(map => map.Item1 == unit.Serial) &&
                        !!this.newSimsToCreate?.find(sim => sim.Iccid == this.preMappedSimsToSerial?.find(mapping => mapping.Item1 == unit.Serial)?.Item2) ? "Yes" : "No")
            }
        ];
    }

    private createEngineerImportTable(): void {

        this.engineerImportTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 60 }),
                createCellFunction: () => this.vTableService.createStatusCell(VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name" }),
                createCellFunction: (user) => this.vTableService.createTableCell(user.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Email" }),
                createCellFunction: (user) => this.vTableService.createTableCell(user.Email)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.ParentCompany" }),
                createCellFunction: (user) => this.vTableService.createTableCell(this.selectedUserGroup.Description)
            }
        ];
    }

    private createJobItemImportTable(): void {

        this.jobItemImportTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "", width: 60 }),
                createCellFunction: () => this.vTableService.createStatusCell(VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CommissionEditor.VehicleRegistrationTitle" }),
                createCellFunction: (result) => this.vTableService.createVehicleRegCell(result.Vehicle, undefined, (!!result.Vehicle.Reg && result.VehicleRequiresCreation))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CsvImporter.NewVehicle" }),
                createCellFunction: (result) => this.vTableService.createTableCell(result.VehicleRequiresCreation || !result.Vehicle?.ShortReg ? "Yes" : "No")
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerJobItemList.Instruction" }),
                createCellFunction: (result) => this.vTableService.createTableCell(result.EngineerJobItem.Comments)
            }
        ];
    }
}
